/**
 * ChildPerspectivesPage — the drill-down view at
 * `/children/:childId/perspectives`.
 *
 * Layout:
 *   - Back link to /dashboard
 *   - Hero: child photo + name + age + partial-birthday pill
 *   - "Pinned" section with all currently-pinned perspectives
 *   - Tier-grouped "Explore more" sections (tiers 1-5) showing the
 *     unpinned, non-expired perspectives
 *   - Collapsible "Expired" section at the bottom for cards that
 *     have aged out
 *
 * Every card supports pin toggle via the optimistic mutation in
 * `useUpdateChildPreferences`. The mutation immediately reflects
 * in the local cache so the card visibly moves to the Pinned
 * section without waiting for the server round trip.
 */

import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { LoadingShell } from "@/components/LoadingShell";
import { usePhotoURL } from "@/lib/usePhotoURL";
import { ActivityCard } from "@/features/activities/ActivityCard";
import { useChildSuggestions } from "@/features/activities/useActivities";
import { useChildren, type Child } from "@/features/children/useChildren";
import {
  useChildPerspectives,
  useUpdateChildPreferences,
  type ComputedPerspective,
} from "@/features/perspectives/usePerspectives";
import { PerspectiveCard } from "@/features/perspectives/PerspectiveCard";
import { SectionHeading, TIER_NAMES } from "@/features/perspectives/primitives";

type Tier = 1 | 2 | 3 | 4 | 5;
const TIERS: Tier[] = [1, 2, 3, 4, 5];

export function ChildPerspectivesPage() {
  const { childId } = useParams<{ childId: string }>();
  const { data: children, isLoading: childrenLoading } = useChildren();
  const {
    data: perspectives,
    isLoading: perspectivesLoading,
    error,
  } = useChildPerspectives(childId ?? null);
  const updatePrefs = useUpdateChildPreferences(childId ?? "");
  const { data: childSuggestions } = useChildSuggestions(childId);
  const [showExpired, setShowExpired] = useState(false);

  const child = useMemo(() => {
    if (children === undefined || typeof childId !== "string") return null;
    return children.find((c) => c.id === childId) ?? null;
  }, [children, childId]);

  if (childrenLoading || perspectivesLoading) {
    return <LoadingShell message="Loading perspectives…" />;
  }

  if (child === null) {
    return (
      <main className="max-w-[960px] mx-auto px-6 md:px-11 pt-16 pb-24">
        <h1 className="font-serif text-display-md text-ink">
          Child not found.
        </h1>
        <Link
          to="/dashboard"
          className="mt-4 inline-flex font-sans text-[14px] text-coral-deep"
        >
          ← Back to dashboard
        </Link>
      </main>
    );
  }

  if (error !== null && error !== undefined) {
    return (
      <main className="max-w-[960px] mx-auto px-6 md:px-11 pt-16 pb-24">
        <h1 className="font-serif text-display-md text-ink">
          Couldn't load perspectives.
        </h1>
        <p className="mt-2 font-sans text-[14px] text-ink-muted">
          Try refreshing.
        </p>
      </main>
    );
  }

  const all = perspectives ?? [];
  const pinned = all.filter((p) => p.pinned && !p.expired);
  const unpinnedByTier = groupByTier(all.filter((p) => !p.pinned && !p.expired));
  const expired = all.filter((p) => p.expired);

  const handleTogglePin = (perspective: ComputedPerspective) => {
    updatePrefs.mutate({
      entries: [{ slug: perspective.slug, pinned: !perspective.pinned }],
    });
  };

  return (
    <main className="max-w-[960px] mx-auto px-6 md:px-11 pt-12 pb-24 flex flex-col gap-10">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-2 font-sans text-[13px] text-ink-muted hover:text-coral-deep w-fit"
      >
        ← Back to dashboard
      </Link>

      <ChildHero child={child} />

      {pinned.length > 0 && (
        <section>
          <SectionHeading
            eyebrow="Pinned"
            title={
              <>
                Your <em className="italic font-normal text-coral-deep">pinned</em> perspectives
              </>
            }
            description="The ones you see on the dashboard."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pinned.map((p) => (
              <PerspectiveCard
                key={p.slug}
                perspective={p}
                onPinToggle={() => handleTogglePin(p)}
                pinDisabled={updatePrefs.isPending}
              />
            ))}
          </div>
        </section>
      )}

      <section>
        <SectionHeading
          eyebrow="Explore more"
          title={
            <>
              The <em className="italic font-normal text-coral-deep">full catalog</em>
            </>
          }
          description="Pin anything here to move it to your dashboard."
        />
        <div className="flex flex-col gap-8">
          {TIERS.map((tier) => {
            const rows = unpinnedByTier[tier];
            if (rows === undefined || rows.length === 0) return null;
            return (
              <div key={tier} className="flex flex-col gap-3">
                <div className="font-sans text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-muted">
                  {TIER_NAMES[tier]} · Tier {tier}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {rows.map((p) => (
                    <PerspectiveCard
                      key={p.slug}
                      perspective={p}
                      onPinToggle={() => handleTogglePin(p)}
                      pinDisabled={updatePrefs.isPending}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {childSuggestions !== undefined && childSuggestions.length > 0 && (
        <section>
          <SectionHeading
            eyebrow="Suggested activities"
            title={
              <>
                Things to do with{" "}
                <em className="italic font-normal text-coral-deep">
                  {child.name}
                </em>
              </>
            }
            description="Matched to their age and the perspectives closing soonest."
          />
          <div className="flex flex-col gap-4">
            {childSuggestions.map((s) => (
              <ActivityCard key={s.id} suggestion={s} />
            ))}
          </div>
        </section>
      )}

      {expired.length > 0 && (
        <section>
          <button
            type="button"
            onClick={() => setShowExpired((s) => !s)}
            className="font-sans text-[13px] text-ink-muted hover:text-coral-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-amber rounded px-2 py-1"
          >
            {showExpired ? "Hide" : "Show"} {expired.length} expired perspectives
          </button>
          {showExpired && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {expired.map((p) => (
                <PerspectiveCard
                  key={p.slug}
                  perspective={p}
                  onPinToggle={() => handleTogglePin(p)}
                  pinDisabled={updatePrefs.isPending}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Child hero
// ---------------------------------------------------------------------------

function ChildHero({ child }: { child: Child }) {
  const { url: photoUrl } = usePhotoURL(child.id);
  const isEstimated = child.birth_month === null || child.birth_day === null;

  return (
    <header className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
      <div className="w-20 h-20 md:w-24 md:h-24 rounded-md bg-surface border border-edge overflow-hidden flex items-center justify-center shrink-0">
        {photoUrl === null ? (
          <div className="font-serif text-[32px] md:text-[40px] text-coral-deep">
            {child.name.slice(0, 1)}
          </div>
        ) : (
          // eslint-disable-next-line jsx-a11y/alt-text
          <img src={photoUrl} alt="" className="w-full h-full object-cover" />
        )}
      </div>
      <div className="flex flex-col gap-1">
        <div className="font-sans text-eyebrow uppercase text-coral-deep font-semibold">
          {child.name}'s perspectives
        </div>
        <h1 className="font-serif text-[24px] md:text-display-md text-ink">
          All <em className="italic font-normal text-coral-deep">25</em> of them
        </h1>
        {isEstimated && (
          <div className="mt-1 font-sans text-[12px] italic text-ink-muted">
            Numbers marked with ~ are estimated because some of {child.name}'s
            birthday details aren't filled in.
          </div>
        )}
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function groupByTier(
  rows: ComputedPerspective[],
): Partial<Record<Tier, ComputedPerspective[]>> {
  const out: Partial<Record<Tier, ComputedPerspective[]>> = {};
  for (const row of rows) {
    const tier = row.tier as Tier;
    if (out[tier] === undefined) out[tier] = [];
    out[tier]!.push(row);
  }
  return out;
}
